// ==============================================================
// Session Tracking Service
// ==============================================================
// Professional-grade session tracking with heartbeat mechanism
// Similar to Google/Meta standards for tracking user activity

import { apiService } from "./api.service";

function isUnauthorizedApiError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

// ==============================================================
// Types
// ==============================================================

export interface Session {
  id: string;
  login_at: string;
  logout_at: string | null;
  duration_seconds: number | null;
  is_active: boolean;
  ip_address?: string;
}

export interface SessionStats {
  totalSessions: number;
  totalHours: number;
  totalDurationSeconds: number;
  averageDurationSeconds: number;
  longestSessionSeconds: number;
}

export interface PaginationInfo {
  limit: number;
  offset: number;
  hasMore: boolean;
  returned: number;
}

export interface UserSessionsResponse {
  sessions: Session[];
  stats?: SessionStats;
  pagination: PaginationInfo;
  timeframe: "daily" | "weekly" | "monthly";
  startDate: string;
  endDate: string;
}

export interface CompanySessionsResponse {
  userStats: Array<{
    userId: string;
    userName: string;
    userEmail: string;
    totalSessions: number;
    totalDurationSeconds: number;
    totalHours: number;
  }>;
  totalCompanySessions: number;
  totalCompanyHours: number;
  timeframe: "daily" | "weekly" | "monthly";
  startDate: string;
  endDate: string;
}

// ==============================================================
// Session Tracking Manager
// ==============================================================
// Handles heartbeat and session lifecycle

class SessionTracker {
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private sessionId: string | null = null;
  private isTracking = false;
  /** Stops heartbeat spam after 401/403 until the next explicit start() (e.g. after login). */
  private authLost = false;
  private readonly HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds
  private visibilityHandler: (() => void) | null = null;
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  /**
   * Start session tracking
   * Should be called after successful login
   */
  async start(): Promise<void> {
    if (this.isTracking) {
      if (import.meta.env.DEV) {
        console.warn("[SessionTracker] Already tracking");
      }
      return;
    }

    this.authLost = false;

    try {
      // Start session on backend
      const response = await sessionAPI.startSession();
      this.sessionId = response.sessionId;
      this.isTracking = true;

      // Start heartbeat
      this.startHeartbeat();

      // Set up visibility change handler to pause/resume tracking
      this.setupVisibilityTracking();

      // Set up beforeunload to end session on tab close
      this.setupBeforeUnloadHandler();

      if (import.meta.env.DEV) {
        console.info("[SessionTracker] Session started", { sessionId: this.sessionId });
      }
    } catch (error) {
      console.error("[SessionTracker] Failed to start session", error);
      if (isUnauthorizedApiError(error)) {
        this.pauseForUnauthorized();
        return;
      }
      // Continue anyway - the heartbeat will create a session if needed
      this.isTracking = true;
      this.startHeartbeat();
    }
  }

  /**
   * Stop session tracking
   * Should be called on logout
   */
  async stop(): Promise<void> {
    if (!this.isTracking) {
      return;
    }

    try {
      // Stop heartbeat first
      this.stopHeartbeat();

      // Remove event listeners
      this.removeEventListeners();

      // End session on backend
      await sessionAPI.endSession();

      if (import.meta.env.DEV) {
        console.info("[SessionTracker] Session ended", { sessionId: this.sessionId });
      }
    } catch (error) {
      console.error("[SessionTracker] Failed to end session", error);
    } finally {
      this.sessionId = null;
      this.isTracking = false;
      this.authLost = false;
    }
  }

  private pauseForUnauthorized(): void {
    if (this.authLost) return;
    this.authLost = true;
    this.stopHeartbeat();
    this.isTracking = false;
    if (import.meta.env.DEV) {
      console.warn("[SessionTracker] Paused: session unauthorized (401/403).");
    }
  }

  /**
   * Start the heartbeat interval
   */
  private startHeartbeat(): void {
    if (this.authLost || this.heartbeatInterval) {
      return;
    }

    // Send initial heartbeat
    this.sendHeartbeat();

    // Set up interval for periodic heartbeats
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Stop the heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Send a heartbeat to the server
   */
  private async sendHeartbeat(): Promise<void> {
    if (!this.isTracking) {
      return;
    }

    try {
      const response = await sessionAPI.heartbeat();
      
      // If a new session was created, update our session ID
      if (response.isNewSession && response.sessionId) {
        this.sessionId = response.sessionId;
        console.info("[SessionTracker] New session created via heartbeat", { sessionId: this.sessionId });
      }
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        this.pauseForUnauthorized();
        return;
      }
      if (import.meta.env.DEV) {
        console.warn("[SessionTracker] Heartbeat failed", error);
      }
    }
  }

  /**
   * Set up visibility change tracking
   * Pauses heartbeat when tab is hidden, resumes when visible
   */
  private setupVisibilityTracking(): void {
    this.visibilityHandler = () => {
      if (document.hidden) {
        // Tab hidden - pause heartbeat (but don't stop completely)
        // We still want to send occasional heartbeats even when hidden
        // to prevent session from being marked as stale
        this.stopHeartbeat();
        // Send one heartbeat when hiding to update activity
        this.sendHeartbeat();
        // Start a slower heartbeat (every 5 minutes when hidden)
        this.heartbeatInterval = setInterval(() => {
          this.sendHeartbeat();
        }, 5 * 60 * 1000);
      } else {
        // Tab visible again - resume normal heartbeat
        if (this.authLost) {
          return;
        }
        this.stopHeartbeat();
        this.startHeartbeat();
      }
    };

    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  /**
   * Set up beforeunload handler to end session on tab close
   */
  private setupBeforeUnloadHandler(): void {
    this.beforeUnloadHandler = () => {
      // Send a final heartbeat using sendBeacon (non-blocking)
      // This ensures the session duration is updated even if the tab closes
      if (this.isTracking) {
        try {
          const url = `${import.meta.env.VITE_API_URL}/api/v1/sessions/end`;
          const token = 
            document.cookie.split("; ").find(row => row.startsWith("sb-access-token="))?.split("=")[1] ||
            localStorage.getItem("sb-access-token") ||
            sessionStorage.getItem("sb-access-token");
          
          if (navigator.sendBeacon && token) {
            const data = new Blob([JSON.stringify({})], { type: "application/json" });
            // Note: sendBeacon doesn't support custom headers, so this might not work
            // with auth. This is a best-effort attempt.
            navigator.sendBeacon(url, data);
          }
        } catch {
          // Ignore errors - best effort only
        }
      }
    };

    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  /**
   * Check if currently tracking
   */
  get isActive(): boolean {
    return this.isTracking;
  }

  /**
   * Get current session ID
   */
  get currentSessionId(): string | null {
    return this.sessionId;
  }
}

// Singleton instance
export const sessionTracker = new SessionTracker();

// ==============================================================
// Session API Endpoints
// ==============================================================

export const sessionAPI = {
  /**
   * Start a new session (called after login)
   */
  startSession: () =>
    apiService.post<{ sessionId: string; sessionStart: string }>(
      "/api/v1/sessions/start"
    ),

  /**
   * End the current session (called on logout)
   */
  endSession: () =>
    apiService.post<{ endedSessions: number }>("/api/v1/sessions/end"),

  /**
   * Send heartbeat to keep session alive
   */
  heartbeat: () =>
    apiService.post<{ sessionId: string; isNewSession: boolean }>(
      "/api/v1/sessions/heartbeat"
    ),

  /**
   * Get current active session
   */
  getCurrentSession: () =>
    apiService.get<{
      session: Session | null;
      hasActiveSession: boolean;
    }>("/api/v1/sessions/current"),

  /**
   * Get sessions for a specific user with pagination
   * @param userId - The user ID to get sessions for
   * @param timeframe - "daily" | "weekly" | "monthly"
   * @param limit - Number of sessions per page (default: 10)
   * @param offset - Offset for pagination (default: 0)
   */
  getUserSessions: (
    userId: string,
    timeframe: "daily" | "weekly" | "monthly" = "daily",
    limit = 10,
    offset = 0
  ) =>
    apiService.get<UserSessionsResponse>(
      `/api/v1/sessions/user/${userId}?timeframe=${timeframe}&limit=${limit}&offset=${offset}`
    ),

  /**
   * Get my own sessions with pagination
   * @param timeframe - "daily" | "weekly" | "monthly"
   * @param limit - Number of sessions per page (default: 10)
   * @param offset - Offset for pagination (default: 0)
   */
  getMySessions: (
    timeframe: "daily" | "weekly" | "monthly" = "daily",
    limit = 10,
    offset = 0
  ) =>
    apiService.get<UserSessionsResponse>(
      `/api/v1/sessions/me?timeframe=${timeframe}&limit=${limit}&offset=${offset}`
    ),

  /**
   * Get all sessions for the company (admin only)
   * @param timeframe - "daily" | "weekly" | "monthly"
   */
  getCompanySessions: (timeframe: "daily" | "weekly" | "monthly" = "daily") =>
    apiService.get<CompanySessionsResponse>(
      `/api/v1/sessions/company?timeframe=${timeframe}`
    ),
};

export default sessionAPI;
